import {
  objectToQueryString,
  setQueryStringParam,
  getQueryStringParams,
  deleteQueryStringParam,
  parseIdsFromQueryParam,
  sanitizeIds,
} from "../../shared/utils";
import tippy from "tippy.js";

// ==============================================================================
// PURE HELPER FUNCTIONS
// These functions have no side effects and can be tested independently
// ==============================================================================

/**
 * Sorts overview data by the specified column and direction
 * @pure
 */
function sortOverviewData(data, sortColumn, sortDirection) {
  let sorted = [...data]; // Don't mutate input

  if (sortColumn === "name") {
    sorted.sort((a, b) => a.name.localeCompare(b.name));
  } else if (sortColumn === "votes") {
    sorted.sort((a, b) => {
      const rankA = a.priority_rank || Infinity;
      const rankB = b.priority_rank || Infinity;
      if (rankA === rankB) return a.name.localeCompare(b.name);
      return rankA - rankB;
    });
  } else if (sortColumn === "score") {
    sorted.sort((a, b) => {
      if (a.score == null && b.score == null)
        return a.name.localeCompare(b.name);
      if (a.score == null) return 1;
      if (b.score == null) return -1;
      if (a.score === b.score) return a.name.localeCompare(b.name);
      return a.score - b.score;
    });
  } else if (sortColumn === "score_change") {
    sorted.sort((a, b) => {
      if (a.score_change == null && b.score_change == null)
        return a.name.localeCompare(b.name);
      if (a.score_change == null) return 1;
      if (b.score_change == null) return -1;
      if (a.score_change === b.score_change)
        return a.name.localeCompare(b.name);
      return a.score_change - b.score_change;
    });
  } else {
    // Sort by triage category count
    sorted.sort((a, b) => {
      const countA = a[sortColumn] || 0;
      const countB = b[sortColumn] || 0;
      if (countA === countB) return a.name.localeCompare(b.name);
      return countA - countB;
    });
  }

  return sortDirection === "desc" ? sorted.reverse() : sorted;
}

/**
 * Sorts filtered results by the specified column and direction
 * @pure
 */
function sortFilteredResults(data, sortBy, sortReverse) {
  if (!data || data.length === 0) return [];

  let sorted = [...data];

  switch (sortBy) {
    case "team":
      sorted.sort((a, b) => {
        const aKey = (a.team_name || a.actor_name || "").toString();
        const bKey = (b.team_name || b.actor_name || "").toString();
        return aKey.localeCompare(bKey);
      });
      break;
    case "driver":
      sorted.sort((a, b) => {
        const aName = (a.driver_name || "").toString();
        const bName = (b.driver_name || "").toString();
        return aName.localeCompare(bName);
      });
      break;
    case "score":
      sorted.sort((a, b) => {
        if (a.score == null && b.score == null) {
          const aKey = (a.team_name || a.actor_name || "").toString();
          const bKey = (b.team_name || b.actor_name || "").toString();
          return aKey.localeCompare(bKey);
        }
        if (a.score == null) return 1;
        if (b.score == null) return -1;
        return a.score - b.score;
      });
      break;
    case "score_change":
      sorted.sort((a, b) => {
        if (a.score_change == null && b.score_change == null) return 0;
        if (a.score_change == null) return 1;
        if (b.score_change == null) return -1;
        return a.score_change - b.score_change;
      });
      break;
    case "comment":
      sorted.sort((a, b) => {
        const aHasComment = !!(a.comment_text && a.comment_text.trim());
        const bHasComment = !!(b.comment_text && b.comment_text.trim());
        return bHasComment - aHasComment;
      });
      break;
    default:
      sorted.sort((a, b) => {
        const aName = (a.actor_name || "").toString();
        const bName = (b.actor_name || "").toString();
        return aName.localeCompare(bName);
      });
  }

  return sortReverse ? sorted.reverse() : sorted;
}

/**
 * Sorts and filters improvements data
 */
function sortImprovements(
  improvements,
  sortColumn,
  sortDirection,
  driverFilter,
) {
  let filtered = driverFilter
    ? improvements.filter((i) => i.factor === driverFilter)
    : [...improvements];

  if (sortColumn === "squad") {
    filtered.sort((a, b) => a.squad.localeCompare(b.squad));
  } else if (sortColumn === "squad_manager") {
    filtered.sort((a, b) => a.manager.name.localeCompare(b.manager.name));
  } else if (sortColumn === "factor") {
    filtered.sort((a, b) => a.factor.localeCompare(b.factor));
  } else if (sortColumn === "sentiment") {
    filtered.sort((a, b) => a.sentiment_change - b.sentiment_change);
  } else if (sortColumn === "priority") {
    filtered.sort((a, b) => -a.priority_change - -b.priority_change);
  }

  return sortDirection === "desc" ? filtered.reverse() : filtered;
}

/**
 * Calculates percentage for a specific triage category
 * @pure
 */
function calculateDriverPercentage(row, triageCategory) {
  const total = row.escalation + row.improvement_area + row.noop;
  const percentage = (100.0 * row[triageCategory]) / total;
  return parseFloat(percentage.toFixed(1));
}

/**
 * Calculates improvement success rate
 * @pure
 */
function calculateImprovementSuccessRate(improvements, numImprovementAreas) {
  const denominator = Math.max(numImprovementAreas, 1);
  return Math.round((improvements.length / denominator) * 100);
}

// ==============================================================================
// FACTORY FUNCTIONS
// ==============================================================================

/**
 * Creates initial state for a triage category (escalation, improvement_area, noop)
 * Eliminates duplication across the three categories
 *
 * State flags (both needed for proper spinner UX):
 * - loading: Controls whether to show spinner vs table in template
 * - dataFetched: Indicates data has been fetched from API
 *
 * When switching sections, loading=true shows spinner while Alpine renders.
 * After data available, loading=false (with requestAnimationFrame) shows table.
 */
function createTriageCategoryState() {
  return {
    loading: true,
    dataFetched: false,
    data: [],
    sortBy: "score",
    sortReverse: false,
    lastFetchParams: null,
  };
}

const TRIAGE_CATEGORIES = ["escalation", "improvement_area", "noop"];

// ==============================================================================
// MAIN COMPONENT
// ==============================================================================

export function triageResultsModal({
  triageResultsUrl,
  teamBreakdownUrl,
  snapshotBaseUrl,
  triageImprovementsUrl,
  filteredResultsUrl,
  exportUrl,
  triageLabels,
}) {
  const sectionFromParams = getQueryStringParams().get("triage_section");
  const validSections = ["overview", "escalation", "improvement_area", "noop"];
  const initialSection = validSections.includes(sectionFromParams)
    ? sectionFromParams
    : "overview";

  return {
    open: false,
    initialLoad: false,
    showImprovementsTab: false,
    squadIds: [],
    tagIds: [],
    branchId: null,
    tab: null,
    triageLabels,
    selectedDriver: null,
    selectedSection: initialSection,
    overviewData: {
      loaded: false,
      data: [],
      numSquads: 0,
      squadsCompleted: 0,
      eligibleSquads: 0,
      expandedDriver: {},
      breakdownLoaded: false,
      breakdownData: {},
      expandedOpt: "escalation",
      sort: "votes",
      sortDir: "asc",
      lastUpdatedFilter: null,
      triageSortMenuOpen: false,
      lastFetchParams: null, // Track params used for last fetch
    },
    improvements: {
      loaded: false,
      data: {},
      sort: "squad",
      sortDir: "asc",
      driverFilter: "",
    },
    escalation: createTriageCategoryState(),
    improvement_area: createTriageCategoryState(),
    noop: createTriageCategoryState(),
    filteredResultsLoading: false,
    comments: {
      open: false,
      loaded: false,
      driver: null,
      triageSelection: "",
      data: [],
    },
    init() {
      const queryParams = getQueryStringParams();
      const tabFromParams = queryParams.get("triage_tab"); // This is also used in app/controllers/kaizen/latest_snapshot_controller.rb
      const updateFromParams = queryParams.get("triage_updated");
      const branchFromParams = queryParams.get("triage_branch");
      const squadIdsFromParams = parseIdsFromQueryParam("squad_ids");
      const tagIdsFromParams = parseIdsFromQueryParam("tag_ids");

      if (squadIdsFromParams?.length) {
        this.squadIds = squadIdsFromParams;
      }
      if (tagIdsFromParams?.length) this.tagIds = tagIdsFromParams;
      if (branchFromParams?.length) this.branchId = branchFromParams;
      if (updateFromParams?.length)
        this.overviewData.lastUpdatedFilter = updateFromParams;

      if (tabFromParams == "responses") {
        this.openResultsModal();
      }

      if (tabFromParams == "improvements") {
        this.openResultsModal();
        this.setTab("improvements");
      }
    },
    openResultsModal() {
      this.open = true;
      this.setTab("responses");
      this.updateQueryParams();
    },
    updateQueryParams() {
      setQueryStringParam("triage_tab", this.tab);
      setQueryStringParam("squad_ids", this.squadIds?.join(",") || null);
      setQueryStringParam("tag_ids", this.tagIds?.join(",") || null);
      setQueryStringParam(
        "triage_updated",
        this.overviewData.lastUpdatedFilter,
      );
      setQueryStringParam("triage_branch", this.branchId);
    },
    clearTriageQueryParams() {
      deleteQueryStringParam("triage_tab");
      deleteQueryStringParam("squad_ids");
      deleteQueryStringParam("tag_ids");
      deleteQueryStringParam("triage_updated");
      deleteQueryStringParam("triage_branch");
      deleteQueryStringParam("triage_section");

      // Clear selected driver when modal closes
      this.selectedDriver = null;
    },
    setTab(tab) {
      if (this.tab === tab) return;

      this.tab = tab;
      this.updateQueryParams();

      if (this.tab == "improvements" && !this.improvements.loaded) {
        this.fetchImprovementsData();
      }

      if (this.tab == "responses") {
        if (!this.overviewData.loaded) {
          this.fetchOverviewData();
        }
        // if we navigate right to noop, load everything. Otherwise, load escalation and improvement_area.
        if (this.selectedSection === "noop") {
          this.fetchFilteredResultsIfNeeded();
        } else {
          this.fetchFilteredResultsIfNeeded(["escalation", "improvement_area"]);
        }
      }
    },
    refetch() {
      const currentParams = this.paramsKey;

      // Invalidate overview data if params changed
      if (this.overviewData.lastFetchParams !== currentParams) {
        this.improvements.loaded = false;
        this.overviewData.loaded = false;

        // Only fetch if we're currently viewing overview/responses tab
        if (this.tab === "responses" && this.selectedSection === "overview") {
          this.fetchOverviewData();
        }
      }

      // Invalidate all filtered triage categories if params changed
      this.invalidateFilteredTriageCategoriesIfNeeded(currentParams);

      // Fetch if currently viewing a filtered triage category
      if (this.isFilteredTriageCategory(this.selectedSection)) {
        this.fetchFilteredResultsIfNeeded();
      }
    },
    changeSquadIds(squadIds) {
      this.squadIds = sanitizeIds(squadIds);
      this.updateQueryParams();
      this.refetch();
    },
    changeTagIds(tagIds) {
      this.tagIds = sanitizeIds(tagIds);
      this.updateQueryParams();
      this.refetch();
    },
    changeBranchId(branchId) {
      this.branchId = branchId;
      this.updateQueryParams();
      this.refetch();
    },
    get params() {
      let paramObj = {
        squad_ids: `${this.squadIds.join(",")}`,
        tag_ids: `${this.tagIds.join(",")}`,
        triage_branch: this.branchId,
      };

      if (this.overviewData.lastUpdatedFilter && this.tab === "responses") {
        paramObj.last_updated_snapshot_id = this.overviewData.lastUpdatedFilter;
      }

      return `?${objectToQueryString(paramObj)}`;
    },
    // Create a stable key for comparing params (without lastUpdatedFilter)
    get paramsKey() {
      return JSON.stringify({
        squadIds: [...this.squadIds].sort(),
        tagIds: [...this.tagIds].sort(),
        branchId: this.branchId,
        lastUpdatedFilter: this.overviewData.lastUpdatedFilter,
      });
    },
    get exportUrl() {
      return `${exportUrl}${this.params}`;
    },
    tooltipIfTruncated(e, fullText) {
      const el = e.target;
      el._tippy?.destroy(); // Delete previous instance

      if (el && el.offsetWidth < el.scrollWidth) {
        tippy(el, {
          content: fullText,
          delay: [250, 0],
          onShow: (instance) => {
            tippy.hideAll({ exclude: instance });
          },
        });
      }
    },

    // Stuff for "Results" tab
    fetchOverviewData() {
      const fetchParams = this.paramsKey;
      fetch(`${triageResultsUrl}${this.params}`)
        .then((response) => response.json())
        .then((resp) => {
          this.overviewData.data = resp.results_by_driver;
          this.overviewData.numSquads = resp.num_squads;
          this.overviewData.squadsCompleted = resp.squads_completed;
          this.overviewData.eligibleSquads = resp.eligible_squads;
          this.overviewData.escalation_pct = resp.escalation_pct;
          this.overviewData.improvement_area_pct = resp.improvement_area_pct;
          this.overviewData.noop_pct = resp.noop_pct;
          this.showImprovementsTab = resp.has_improvements_data;
          this.overviewData.loaded = true;
          this.overviewData.lastFetchParams = fetchParams;
          this.initialLoad = true;

          if (resp.results_by_driver.length)
            this.expandDriver(this.sortedOverviewData[0]);
        });
    },
    expandDriver(driver) {
      this.overviewData.breakdownLoaded = false;
      this.overviewData.expandedDriver = driver;

      fetch(
        `${teamBreakdownUrl}${this.params}&factor_id=${this.overviewData.expandedDriver.id}`,
      )
        .then((response) => response.json())
        .then((resp) => {
          this.overviewData.breakdownData = resp;
          this.overviewData.breakdownLoaded = true;
        });
    },
    openComments(driver, triageSelection) {
      this.comments.loaded = false;
      this.comments.open = true;
      this.comments.driver = driver;
      this.comments.triageSelection = triageSelection;

      fetch(
        `${snapshotBaseUrl}/comments${this.params}&factor_id=${driver.id}&triage_selection=${triageSelection}`,
      )
        .then((response) => response.json())
        .then((resp) => {
          this.comments.data = resp;
          this.comments.loaded = true;
        });
    },
    get sortedOverviewData() {
      return sortOverviewData(
        this.overviewData.data,
        this.overviewData.sort,
        this.overviewData.sortDir,
      );
    },
    driverPercentageForCategory(r, triageCategory) {
      return calculateDriverPercentage(r, triageCategory);
    },
    optIsExpanded(opt) {
      return this.overviewData.expandedOpt == opt;
    },
    toggleOptExpanded(opt) {
      if (this.optIsExpanded(opt)) {
        this.overviewData.expandedOpt = "";
        return;
      }

      this.overviewData.expandedOpt = opt;
    },
    changeOverviewSort(col) {
      const { sort, sortDir } = this.overviewData;

      // Close triage menu if open
      this.overviewData.triageSortMenuOpen = false;

      if (sort == col) {
        this.overviewData.sortDir = sortDir == "desc" ? "asc" : "desc";
        return;
      }

      this.overviewData.sort = col;
      this.overviewData.sortDir = "asc";
    },
    isTriageSort() {
      return TRIAGE_CATEGORIES.includes(this.overviewData.sort);
    },
    toggleTriageSortMenu() {
      this.overviewData.triageSortMenuOpen =
        !this.overviewData.triageSortMenuOpen;
    },
    closeTriageSortMenu() {
      this.overviewData.triageSortMenuOpen = false;
    },
    selectTriageSort(triageCategory) {
      this.overviewData.sort = triageCategory;
      this.overviewData.sortDir = "desc";
      this.overviewData.triageSortMenuOpen = false;
    },
    changeLastUpdatedFilter(snapshotId) {
      this.overviewData.lastUpdatedFilter = snapshotId;
      this.updateQueryParams();
      this.refetch();
    },
    // Explicit section orchestration functions - called from template @click handlers
    openOverviewSection() {
      this.selectSection("overview");
    },
    openEscalationSection() {
      this.selectSection("escalation");
      this.ensureSectionDataFetched("escalation");
      this.ensureSectionRendered("escalation");
    },
    openImprovementAreaSection() {
      this.selectSection("improvement_area");
      this.ensureSectionDataFetched("improvement_area");
      this.ensureSectionRendered("improvement_area");
    },
    openNoopSection() {
      this.selectSection("noop");
      this.ensureSectionDataFetched("noop");
      this.ensureSectionRendered("noop");
    },
    // Updates which section is selected (UI state only)
    // Used for programmatic navigation (selectDriver, clearSelectedDriver)
    selectSection(section) {
      this.selectedSection = section;
      if (this.isFilteredTriageCategory(section)) {
        this[section].loading = true;
      }
      setQueryStringParam("triage_section", section);
    },
    selectDriver(driver) {
      this.selectedDriver = driver;

      // Invalidate all filtered triage categories (driver filter changes the data)
      this.invalidateFilteredTriageCategories();

      // Switch to escalation tab when driver is selected (can't show overview)
      if (!this.selectedSection || this.selectedSection === "overview") {
        this.openEscalationSection();
      } else {
        // If already on a filtered triage category, trigger fetch
        this.fetchFilteredResultsIfNeeded();
      }
    },
    clearSelectedDriver() {
      this.selectedDriver = null;

      // Invalidate all filtered triage categories (removing driver filter changes the data)
      this.invalidateFilteredTriageCategories();

      // Switch back to overview when clearing driver selection
      this.selectSection("overview");
    },
    // Helper: Check if a section is a filtered triage category
    isFilteredTriageCategory(section) {
      return TRIAGE_CATEGORIES.includes(section);
    },

    // Ensure data is fetched for a specific section
    // Returns immediately if data already fetched or if fetch is in progress
    ensureSectionDataFetched(section) {
      if (!this.isFilteredTriageCategory(section)) return;
      if (this[section].dataFetched) return; // Already have data
      if (this.filteredResultsLoading) return; // Fetch already in progress

      this.fetchFilteredResultsIfNeeded([section]);
    },

    // Ensure a section's data is rendered in the DOM
    // With x-show, table is already in DOM, just hide spinner when data is ready
    ensureSectionRendered(section) {
      if (!this.isFilteredTriageCategory(section)) return;
      if (!this[section].dataFetched) return; // Can't render without data
      if (!this[section].loading) return; // Already rendered

      // With x-show, table is already in DOM, just hide spinner
      this[section].loading = false;
    },

    // Helper: Invalidate all filtered triage categories
    invalidateFilteredTriageCategories() {
      TRIAGE_CATEGORIES.forEach((categoryName) => {
        this[categoryName].loading = true;
        this[categoryName].dataFetched = false;
      });
    },

    // Helper: Invalidate filtered triage categories only if params have changed
    invalidateFilteredTriageCategoriesIfNeeded(currentParams) {
      TRIAGE_CATEGORIES.forEach((triageCategory) => {
        if (this[triageCategory].lastFetchParams !== currentParams) {
          this[triageCategory].loading = true;
          this[triageCategory].dataFetched = false;
        }
      });
    },

    // Fetch filtered results only if needed (not already loaded and not already loading)
    fetchFilteredResultsIfNeeded(categories = TRIAGE_CATEGORIES) {
      // Check if any category needs data
      const needsLoad = categories.some(
        (triageCategory) => !this[triageCategory].dataFetched,
      );

      if (!needsLoad || this.filteredResultsLoading) return;

      this.filteredResultsLoading = true;
      const fetchParams = this.paramsKey;

      let url = `${filteredResultsUrl}${this.params}`;
      if (this.selectedDriver) {
        url += `&factor_id=${this.selectedDriver.id}`;
      }

      fetch(url)
        .then((response) => response.json())
        .then((resp) => {
          const results = resp.results || {};

          categories.forEach((triageCategory) => {
            this[triageCategory].data = results[triageCategory] || [];
            this[triageCategory].dataFetched = true;
            this[triageCategory].lastFetchParams = fetchParams;
            this[triageCategory].loading = false;
          });
        })
        .catch((error) => {
          console.error("Error fetching filtered results:", error);
          categories.forEach((triageCategory) => {
            if (!this[triageCategory].dataFetched) {
              this[triageCategory].data = [];
              this[triageCategory].dataFetched = true;
              this[triageCategory].loading = false; // Show empty state
              this[triageCategory].lastFetchParams = fetchParams;
            }
          });
        })
        .finally(() => {
          this.filteredResultsLoading = false;
        });
    },
    changeFilteredResultsSort(triageCategory, col) {
      const currentSort = this[triageCategory].sortBy;
      const currentReverse = this[triageCategory].sortReverse;

      if (currentSort === col) {
        this[triageCategory].sortReverse = !currentReverse;
      } else {
        this[triageCategory].sortBy = col;
        this[triageCategory].sortReverse = false;
      }
    },
    isFilteredResultsSortOrder(triageCategory, col) {
      return this[triageCategory].sortBy === col;
    },
    getSortedFilteredResults(triageCategory) {
      return sortFilteredResults(
        this[triageCategory].data,
        this[triageCategory].sortBy,
        this[triageCategory].sortReverse,
      );
    },

    // Stuff for "Improvements" tab
    fetchImprovementsData() {
      fetch(`${triageImprovementsUrl}${this.params}`)
        .then((response) => response.json())
        .then((resp) => {
          this.improvements.data = resp;
          this.improvements.loaded = true;
        });
    },
    get improvementAreaSuccessRate() {
      const { improvements = [], num_improvement_areas = 0 } =
        this.improvements.data;
      return calculateImprovementSuccessRate(
        improvements,
        num_improvement_areas,
      );
    },
    get sortedImprovements() {
      return sortImprovements(
        this.improvements.data.improvements,
        this.improvements.sort,
        this.improvements.sortDir,
        this.improvements.driverFilter,
      );
    },
    changeImprovementsSort(col) {
      const { sort, sortDir } = this.improvements;

      if (sort == col) {
        this.improvements.sortDir = sortDir == "desc" ? "asc" : "desc";
        return;
      }

      this.improvements.sort = col;
      this.improvements.sortDir = "desc";
    },
    onChangeDriverFilter(e) {
      this.improvements.driverFilter = e.target.value;
    },
    percentageOfResponses(item, positionCount) {
      if (item.count === 0) return "0%";
      return `${Math.round((100 * item[positionCount]) / item.count)}%`;
    },
  };
}
